import React, { useEffect, useState } from 'react';
import {
  attachmentFileExtension,
  isImageAttachment,
  resolveAttachmentMessageId,
} from '../../email/attachmentUtils';
import type { EmAttachment } from '../../email/types';
import { Icon } from '../Icon/Icon';

export interface EmailAttachmentPreviewProps {
  attachment: EmAttachment;
  messageId: string;
  enabled: boolean;
  onOpen?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  onDownload?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  loadPreviewUrl?: ((attachment: EmAttachment, messageId: string) => Promise<string | null>) | undefined;
}

const thumbBtnStyle: React.CSSProperties = {
  flex: 'none',
  width: 72,
  height: 72,
  padding: 0,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--cream-2)',
  background: 'var(--cream-1)',
  overflow: 'hidden',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const actionBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  flex: 1,
  minWidth: 0,
  height: 28,
  padding: '0 10px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--cream-2)',
  background: 'var(--cream-0)',
  color: 'var(--ink-2)',
  fontSize: 11.5,
  fontWeight: 600,
  fontFamily: 'var(--font-body)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

export function EmailAttachmentPreview({
  attachment,
  messageId,
  enabled,
  onOpen,
  onDownload,
  loadPreviewUrl,
}: EmailAttachmentPreviewProps) {
  const resolvedMessageId = resolveAttachmentMessageId(attachment, messageId);
  const hasRemotePreview = Boolean(attachment.previewUrl);
  const canFetch = Boolean(attachment.id);
  const canOpen = Boolean(onOpen && (canFetch || hasRemotePreview));
  const canDownload = Boolean(onDownload && (canFetch || hasRemotePreview));
  const isImage = isImageAttachment(attachment.name, attachment.contentType);
  const [previewUrl, setPreviewUrl] = useState<string | null>(attachment.previewUrl ?? null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (attachment.previewUrl) {
      setPreviewUrl(attachment.previewUrl);
      setLoadingPreview(false);
      return;
    }

    if (!enabled || !isImage || !loadPreviewUrl || !attachment.id) {
      setPreviewUrl(null);
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setLoadingPreview(true);

    void loadPreviewUrl(attachment, resolvedMessageId)
      .then((url) => {
        if (cancelled) {
          if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url?.startsWith('blob:') ? url : null;
        setPreviewUrl(url);
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, enabled, isImage, loadPreviewUrl, resolvedMessageId]);

  const ext = attachmentFileExtension(attachment.name);

  const thumbContent = previewUrl ? (
    <img
      src={previewUrl}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  ) : loadingPreview ? (
    <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>…</span>
  ) : isImage ? (
    <Icon kind="image" size={20} style={{ color: 'var(--ai-3)' }} />
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <Icon kind="attach" size={18} style={{ color: 'var(--ink-3)' }} />
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          fontWeight: 700,
          color: 'var(--ink-4)',
          letterSpacing: '0.06em',
        }}
      >
        {ext}
      </span>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        maxWidth: 360,
        padding: '10px 12px',
        borderRadius: 'var(--r-md)',
        border: '1px solid var(--cream-2)',
        background: 'var(--cream-0)',
      }}
    >
      {canOpen ? (
        <button
          type="button"
          title={`Preview ${attachment.name}`}
          onClick={() => onOpen?.(attachment, resolvedMessageId)}
          style={{ ...thumbBtnStyle, cursor: 'pointer' }}
        >
          {thumbContent}
        </button>
      ) : (
        <div style={{ ...thumbBtnStyle, cursor: 'default' }}>{thumbContent}</div>
      )}

      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div
            title={attachment.name}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--ink-0)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {attachment.name}
          </div>
          {attachment.size ? (
            <div
              style={{
                marginTop: 2,
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-4)',
                whiteSpace: 'nowrap',
              }}
            >
              {attachment.size}
            </div>
          ) : null}
        </div>

        {canOpen || canDownload ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canOpen ? (
              <button
                type="button"
                title={`Preview ${attachment.name}`}
                onClick={() => onOpen?.(attachment, resolvedMessageId)}
                style={actionBtnStyle}
              >
                <Icon kind="external" size={12} />
                Preview
              </button>
            ) : null}
            {canDownload ? (
              <button
                type="button"
                title={`Download ${attachment.name}`}
                onClick={() => onDownload?.(attachment, resolvedMessageId)}
                style={actionBtnStyle}
              >
                <Icon kind="download" size={12} />
                Download
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface EmailAttachmentPreviewListProps {
  attachments: EmAttachment[];
  messageId: string;
  enabled: boolean;
  onOpenAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  onDownloadAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  loadAttachmentPreview?: ((attachment: EmAttachment, messageId: string) => Promise<string | null>) | undefined;
}

export function EmailAttachmentPreviewList({
  attachments,
  messageId,
  enabled,
  onOpenAttachment,
  onDownloadAttachment,
  loadAttachmentPreview,
}: EmailAttachmentPreviewListProps) {
  if (!attachments.length) return null;

  return (
    <div
      style={{
        marginTop: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
      }}
    >
      {attachments.map((attachment, index) => (
        <EmailAttachmentPreview
          key={attachment.id ?? `${messageId}-${index}`}
          attachment={attachment}
          messageId={messageId}
          enabled={enabled}
          onOpen={onOpenAttachment}
          onDownload={onDownloadAttachment}
          loadPreviewUrl={loadAttachmentPreview}
        />
      ))}
    </div>
  );
}
